import { Dropdown, Input, MonoLabel, cn, focusRing } from '@/design-system';
import {
    ORDER_FILTERS,
    ORDER_SORTS,
    filterFor,
    type InspectionType,
    type OrderSort,
} from '@/entities/order';

import { inspectionsCopy } from '../copy';

export interface FilterBarProps {
    /** Aantal per filterpil; ontbreekt zolang de tellers laden. */
    counts: Record<string, number> | undefined;
    services: readonly InspectionType[];
    status: string;
    search: string;
    service: string;
    sort: OrderSort;
    onStatusChange: (key: string) => void;
    onSearchChange: (value: string) => void;
    onServiceChange: (value: string) => void;
    onSortChange: (value: OrderSort) => void;
}

/**
 * De filterbalk boven de werklijst: statuspillen met hun aantal, een zoekveld
 * en twee keuzelijsten. De tellers staan op de pil zelf, zodat je ziet waar het
 * werk zit zonder te klikken; ze volgen de zoekterm en de dienst.
 *
 * Een filter zonder eigen pil, zoals *Wacht op afspraak* vanaf de Planning,
 * staat zolang hij aan staat als extra pil achteraan; met *Alle* is hij weg.
 *
 * Zoekveld en keuzelijsten zijn even hoog (42px) en dragen het icoon van het
 * merkblad: zoeken, filteren, sorteren.
 */
export function FilterBar({
    counts,
    services,
    status,
    search,
    service,
    sort,
    onStatusChange,
    onSearchChange,
    onServiceChange,
    onSortChange,
}: FilterBarProps) {
    const pills = ORDER_FILTERS.some((filter) => filter.key === status)
        ? ORDER_FILTERS
        : [...ORDER_FILTERS, filterFor(status)];

    return (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--border-default)] bg-[var(--surface-card)] px-6 py-3">
            <div
                role="group"
                aria-label={inspectionsCopy.filterGroupLabel}
                className="flex flex-wrap gap-2"
            >
                {pills.map((filter) => {
                    const active = filter.key === status;

                    return (
                        <button
                            key={filter.key}
                            type="button"
                            aria-pressed={active}
                            onClick={() => onStatusChange(filter.key)}
                            className={cn(
                                'inline-flex min-h-[40px] cursor-pointer items-center rounded-[20px] border px-[13px] py-[9px] text-[13px] leading-none',
                                'font-[family-name:var(--font-body)] transition-colors duration-100',
                                active
                                    ? 'border-[var(--accent)] bg-[var(--accent)] font-medium text-[var(--text-on-dark)]'
                                    : 'border-[var(--border-control)] bg-[var(--surface-card)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--accent)]',
                                focusRing,
                            )}
                        >
                            {filter.label}
                            <MonoLabel
                                size={10}
                                variant="value"
                                tone={active ? 'onDark' : 'muted'}
                                className="ml-[7px] opacity-65"
                            >
                                {counts?.[filter.key] ?? '·'}
                            </MonoLabel>
                        </button>
                    );
                })}
            </div>

            <div className="min-w-[12px] flex-1" />

            <div className="flex flex-wrap items-center gap-2">
                <Input
                    size="sm"
                    type="search"
                    icon="search"
                    className="w-[248px]"
                    aria-label={inspectionsCopy.searchLabel}
                    placeholder={inspectionsCopy.searchPlaceholder}
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                />

                <Dropdown
                    size="sm"
                    icon="filter"
                    aria-label={inspectionsCopy.serviceLabel}
                    className="max-w-[260px]"
                    value={service}
                    options={[
                        { value: '', label: inspectionsCopy.serviceAll },
                        ...services.map((type) => ({ value: type.code, label: type.name })),
                    ]}
                    onChange={onServiceChange}
                />

                <Dropdown
                    size="sm"
                    icon="sort"
                    align="end"
                    prefix={inspectionsCopy.sortPrefix}
                    aria-label={inspectionsCopy.sortLabel}
                    value={sort}
                    options={ORDER_SORTS.map((option) => ({
                        value: option.key,
                        label: option.label,
                    }))}
                    onChange={onSortChange}
                />
            </div>
        </div>
    );
}
